import { ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Typography,
  IconButton,
  Button,
  DialogProps,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface RenderDialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  title?: string | ReactNode;
  subtitle?: string;

  actions?: ReactNode;
  onSave?: () => void;
  isSaving?: boolean;
  saveButtonText?: string;
  saveButtonEndIcon?: ReactNode;
  cancelButtonText?: string;

  isDisabled?: boolean;
  PaperProps?: DialogProps["PaperProps"];

  sx?: DialogProps["sx"];
  DialogTitleProps?: {
    sx?: object;
    className?: string;
  };
  DialogActionsProps?: {
    sx?: object;
    className?: string;
  };
  titleContainerClassName?: string;
  actionsContainerClassName?: string;

  wrapContentInDialogContent?: boolean;
  DialogContentProps?: {
    sx?: object;
    className?: string;
  };
}

export function RenderDialog({
  open,
  onClose,
  children,
  size = "md",
  title,
  subtitle,
  actions,
  onSave,
  isSaving = false,
  saveButtonText = "Save",
  saveButtonEndIcon,
  cancelButtonText = "Cancel",
  isDisabled = false,
  PaperProps,
  sx,
  DialogTitleProps,
  DialogActionsProps,
  titleContainerClassName,
  actionsContainerClassName,
  wrapContentInDialogContent = false,
  DialogContentProps,
}: RenderDialogProps) {

  const renderedTitle =
    typeof title === "string" ? (
      <DialogTitle
        className={"px-0"}
        {...DialogTitleProps}
        sx={{
          ...DialogTitleProps?.sx,
        }}
      >
        <div
          className={
            titleContainerClassName ??
            "flex flex-col border-solid border-border border-0 border-b-[1px] pb-2"
          }
        >
          <div className={"flex items-center justify-between px-4"}>
            <Typography variant={"h6"}>{title}</Typography>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
          {subtitle && (
            <Typography
              variant={"caption"}
              className={"px-4 pb-2 text-dark-text-disabled"}
            >
              {subtitle}
            </Typography>
          )}
        </div>
      </DialogTitle>
    ) : (
      title
    );

  const renderedActions =
    actions !== undefined ? (
      actions
    ) : onSave ? (
      <DialogActions
        className={"px-0"}
        {...DialogActionsProps}
        sx={{
          ...DialogActionsProps?.sx,
        }}
      >
        <div
          className={
            actionsContainerClassName ??
            "flex w-full justify-between border-solid border-border border-0 border-t-[1px] px-2 pt-1"
          }
        >
          <Button color={"inherit"} onClick={onClose} disabled={isSaving}>
            {cancelButtonText}
          </Button>
          <Button
            color={"primary"}
            onClick={onSave}
            disabled={isDisabled || isSaving}
            endIcon={saveButtonEndIcon ?? null}
          >
            {isSaving ? "Saving..." : saveButtonText}
          </Button>
        </div>
      </DialogActions>
    ) : null;

  const renderedContent = wrapContentInDialogContent ? (
    <DialogContent
      {...DialogContentProps}
      sx={{
        ...DialogContentProps?.sx,
      }}
    >
      {children}
    </DialogContent>
  ) : (
    children
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={size}
      fullWidth
      sx={sx}
      slotProps={{
        paper: PaperProps
      }}
    >
      {renderedTitle}
      {renderedContent}
      {renderedActions}
    </Dialog>
  );
}

export default RenderDialog;